import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationImportantOutlined from '@mui/icons-material/NotificationImportantOutlined';
import {
  useClearStorageCredential,
  useSigninSilentWithLock,
} from '@vs/oidc-client';
import {
  BaseGlobalState,
  toggleAuthFailed,
  toggleSessionExpired,
} from '@vs/rtk/slices/genericGlobal';
import { PortalButton } from '@vs/ui/portal/button';
import { PortalDialog } from '@vs/ui/portal/dialog';
import { useTranslation } from 'react-i18next';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

const useStateSelector: TypedUseSelectorHook<{ general: BaseGlobalState }> =
  useSelector;

/**
 * Show global un-auth error meesage by redux state.
 * * Redux store need to include `generalStateSlice`
 *
 * @export
 * @returns {ReactElement}
 */
export function DialogUnAuth() {
  const dispatch = useDispatch();
  const { t } = useTranslation('shared');
  const clearStorageCredential = useClearStorageCredential();
  const signinSilentWithLock = useSigninSilentWithLock();
  const isSessionExpired = useStateSelector(s => s.general.isSessionExpired);
  const isAuthFailed = useStateSelector(s => s.general.isAuthFailed);

  async function handleSilentReAuth() {
    try {
      dispatch(toggleAuthFailed(false));
      await signinSilentWithLock();
    } catch (error) {
      dispatch(toggleSessionExpired(true));
    }
  }

  async function handleReLogin() {
    try {
      await clearStorageCredential();
    } finally {
      window.location.reload();
    }
  }

  return (
    <>
      <PortalDialog
        size="sm"
        open={isAuthFailed}
        title={t('app_boundary.auth_failed.title', {
          defaultValue: 'User authentication failed',
        })}
        content={t('app_boundary.auth_failed.desc', {
          defaultValue: 'Please try again.',
        })}
        titleIcon={
          <InfoOutlinedIcon className="text-[32px] me-2 text-vs-neutral-focus" />
        }
        actions={
          <div className="text-right">
            <PortalButton
              label={t('common.btn.close', {
                defaultValue: 'Close',
              })}
              onClick={handleSilentReAuth}
              type="secondary"
              variant="contained"
            />
          </div>
        }
      />
      <PortalDialog
        className="z-[1400]"
        size="sm"
        open={isSessionExpired}
        title={t('system.toast.session_expired', {
          defaultValue: 'Session expired',
        })}
        content={t('app_boundary.session_expired.desc', {
          defaultValue: 'Please reload and login again.',
        })}
        titleIcon={
          <NotificationImportantOutlined className="text-[32px] me-2 text-vs-error-focus" />
        }
        actions={
          <div className="text-right">
            <PortalButton
              label={t('common.btn.reload', {
                defaultValue: 'Reload',
              })}
              onClick={handleReLogin}
              type="secondary"
              variant="contained"
            />
          </div>
        }
      />
    </>
  );
}
